/**
 * 📝 [Phase 103.1 — 2026-07-09] Éditeur de critères d'évaluation.
 *
 * Mode inline dans le formulaire d'éval : permet à un admin de renommer,
 * ajouter, supprimer et réorganiser les critères + catégories. Le résultat
 * (structure `{categories: [...]}`) est passé via `onChange` au parent.
 *
 * Ne persiste rien lui-même — la propagation au template ou uniquement dans
 * l'éval courante est décidée par le parent via dialog de confirmation.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function CriteriaTemplateEditor({ template, onChange, onCancel, onDone }) {
  const [categories, setCategories] = useState(template?.categories || []);
  const [expandedCat, setExpandedCat] = useState({});

  const propagate = (newCats) => {
    setCategories(newCats);
    onChange?.({ categories: newCats });
  };

  const updateCat = (idx, patch) => {
    const next = categories.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    propagate(next);
  };

  const removeCat = (idx) => {
    const next = categories.filter((_, i) => i !== idx);
    propagate(next);
  };

  const moveCat = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= categories.length) return;
    const next = [...categories];
    [next[idx], next[j]] = [next[j], next[idx]];
    propagate(next);
  };

  const addCat = () => {
    const code = `cat_${Date.now()}`;
    propagate([...categories, { code, label: "Nouvelle catégorie", criteres: [] }]);
    setExpandedCat({ ...expandedCat, [code]: true });
  };

  const updateCritere = (catIdx, critIdx, patch) => {
    const next = categories.map((c, i) => {
      if (i !== catIdx) return c;
      const newCrits = c.criteres.map((cc, j) => (j === critIdx ? { ...cc, ...patch } : cc));
      return { ...c, criteres: newCrits };
    });
    propagate(next);
  };

  const removeCritere = (catIdx, critIdx) => {
    const next = categories.map((c, i) => {
      if (i !== catIdx) return c;
      return { ...c, criteres: c.criteres.filter((_, j) => j !== critIdx) };
    });
    propagate(next);
  };

  const moveCritere = (catIdx, critIdx, dir) => {
    const j = critIdx + dir;
    const cat = categories[catIdx];
    if (!cat || j < 0 || j >= cat.criteres.length) return;
    const newCrits = [...cat.criteres];
    [newCrits[critIdx], newCrits[j]] = [newCrits[j], newCrits[critIdx]];
    updateCat(catIdx, { criteres: newCrits });
  };

  const addCritere = (catIdx) => {
    const code = `crit_${Date.now()}`;
    const cat = categories[catIdx];
    updateCat(catIdx, {
      criteres: [...(cat.criteres || []), { code, label: "Nouveau critère" }],
    });
  };

  return (
    <div className="space-y-3" data-testid="criteria-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-600">
          Modifie librement les critères. Rien n&apos;est propagé au modèle par défaut
          tant que tu ne l&apos;as pas choisi à la sauvegarde.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid="criteria-editor-cancel"
          >
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
            onClick={() => onDone?.({ categories })}
            data-testid="criteria-editor-done"
          >
            Terminer l&apos;édition
          </Button>
        </div>
      </div>

      {categories.map((cat, catIdx) => {
        const isOpen = expandedCat[cat.code] !== false;
        return (
          <Card
            key={cat.code || catIdx}
            className="border border-slate-200"
            data-testid={`criteria-editor-cat-${catIdx}`}
          >
            <CardContent className="pt-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={cat.label || ""}
                  onChange={(e) =>
                    updateCat(catIdx, { label: e.target.value, code: cat.code || slugify(e.target.value) })
                  }
                  className="font-semibold"
                  data-testid={`criteria-editor-cat-label-${catIdx}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedCat({ ...expandedCat, [cat.code]: !isOpen })}
                  data-testid={`criteria-editor-cat-toggle-${catIdx}`}
                >
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveCat(catIdx, -1)}
                  disabled={catIdx === 0}
                  title="Monter"
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveCat(catIdx, +1)}
                  disabled={catIdx === categories.length - 1}
                  title="Descendre"
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (window.confirm(`Supprimer la catégorie "${cat.label}" ?`)) removeCat(catIdx);
                  }}
                  className="text-red-600"
                  title="Supprimer la catégorie"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {isOpen && (
                <div className="pl-4 border-l border-slate-200 space-y-1.5">
                  {(cat.criteres || []).map((crit, critIdx) => (
                    <div
                      key={crit.code || critIdx}
                      className="flex items-center gap-2"
                      data-testid={`criteria-editor-crit-${catIdx}-${critIdx}`}
                    >
                      <Input
                        value={crit.label || ""}
                        onChange={(e) =>
                          updateCritere(catIdx, critIdx, {
                            label: e.target.value,
                            code: crit.code || slugify(e.target.value),
                          })
                        }
                        placeholder="Nom du critère"
                        className="text-sm"
                        data-testid={`criteria-editor-crit-label-${catIdx}-${critIdx}`}
                      />
                      <Button variant="ghost" size="icon" onClick={() => moveCritere(catIdx, critIdx, -1)} disabled={critIdx === 0}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveCritere(catIdx, critIdx, +1)} disabled={critIdx === (cat.criteres.length - 1)}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCritere(catIdx, critIdx)}
                        className="text-red-600"
                        data-testid={`criteria-editor-crit-remove-${catIdx}-${critIdx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addCritere(catIdx)}
                    className="mt-1"
                    data-testid={`criteria-editor-cat-add-crit-${catIdx}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un critère
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Button variant="outline" onClick={addCat} data-testid="criteria-editor-add-cat">
        <Plus className="w-4 h-4 mr-1" /> Ajouter une catégorie
      </Button>
    </div>
  );
}

export default CriteriaTemplateEditor;
